import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuthStore } from "@/store/auth";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useAronUiStore } from "../store/aronUiStore";
import {
  clampRect,
  defaultRect,
  rectForZone,
  resizeRect,
  snapZoneFor,
  type FloatRect,
  type ResizeDir,
  type SnapZone,
} from "../utils/floatGeometry";
import { AronFloatWindow } from "./AronFloatWindow";

/** Chwyty skalowania: krawędzie + rogi, w kolejności rysowania. */
const HANDLES: { dir: ResizeDir; cls: string }[] = [
  { dir: "n", cls: "top-0 right-2 left-2 h-1.5 cursor-ns-resize" },
  { dir: "s", cls: "right-2 bottom-0 left-2 h-1.5 cursor-ns-resize" },
  { dir: "w", cls: "top-2 bottom-2 left-0 w-1.5 cursor-ew-resize" },
  { dir: "e", cls: "top-2 right-0 bottom-2 w-1.5 cursor-ew-resize" },
  { dir: "nw", cls: "top-0 left-0 h-2.5 w-2.5 cursor-nwse-resize" },
  { dir: "ne", cls: "top-0 right-0 h-2.5 w-2.5 cursor-nesw-resize" },
  { dir: "sw", cls: "bottom-0 left-0 h-2.5 w-2.5 cursor-nesw-resize" },
  { dir: "se", cls: "right-0 bottom-0 h-2.5 w-2.5 cursor-nwse-resize" },
];

const viewport = () => ({ vw: window.innerWidth, vh: window.innerHeight });

/**
 * Pływające okno rozmowy — trzecia postać wydzielonego ARON-a, obok panelu z
 * prawej i pełnej strony. Leży NAD stroną (portal + `fixed`), więc nie tnie go
 * żaden `overflow` i nie drga przy przewijaniu; przeciąga się belką, skaluje
 * krawędziami, przyciąga do brzegów ekranu i zwija do samej belki.
 *
 * Gest liczy się poza Reactem — pozycja idzie prosto na styl elementu, a do
 * store'u (i localStorage) trafia dopiero po puszczeniu myszy. Bez tego każdy
 * piksel ruchu przerysowywałby całą rozmowę.
 *
 * Tylko desktop: na telefonie pływak jest zabawką, więc poniżej `md` nie
 * powstaje w ogóle. Wyjście z trybu: „Przypnij do boku" w belce — wraca dokładnie
 * to, co było wcześniej.
 */
export function AronFloatHost() {
  const user = useAuthStore((s) => s.user);
  const isDesktop = useIsDesktop();
  const open = useAronUiStore((s) => s.dockOpen);
  const mode = useAronUiStore((s) => s.dockMode);
  const storedRect = useAronUiStore((s) => s.floatRect);
  const setFloatRect = useAronUiStore((s) => s.setFloatRect);
  const restoreFloatRect = useAronUiStore((s) => s.restoreFloatRect);
  const restoreRect = useAronUiStore((s) => s.floatRestoreRect);
  const minimized = useAronUiStore((s) => s.floatMinimized);
  const setMinimized = useAronUiStore((s) => s.setFloatMinimized);

  const boxRef = useRef<HTMLDivElement>(null);
  const [snap, setSnap] = useState<SnapZone>(null);
  const [gesture, setGesture] = useState<"move" | "resize" | null>(null);

  const canUse =
    !!user &&
    user.role !== "contractor" &&
    !!user.accessible_dlc_modules?.includes("dlc_03");
  const active = canUse && open && mode === "float" && isDesktop;

  // Zapamiętana pozycja z większego monitora (albo po zmianie rozmiaru okna)
  // musi wrócić na ekran — inaczej rozmowa „znika" za krawędzią.
  useEffect(() => {
    if (!active) return;
    const fit = () => {
      const { vw, vh } = viewport();
      const next = clampRect(storedRect ?? defaultRect(vw, vh), vw, vh);
      if (
        !storedRect ||
        next.x !== storedRect.x ||
        next.y !== storedRect.y ||
        next.w !== storedRect.w ||
        next.h !== storedRect.h
      ) {
        setFloatRect(next);
      }
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [active, storedRect, setFloatRect]);

  if (!active) return null;

  const rect = storedRect ?? defaultRect(window.innerWidth, window.innerHeight);

  /** Wspólny szkielet gestu: rysuj na elemencie, zatwierdź na mouseup. */
  const runGesture = (
    e: React.MouseEvent,
    kind: "move" | "resize",
    compute: (dx: number, dy: number, ev: MouseEvent) => FloatRect,
    from: FloatRect = rect,
  ) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    setGesture(kind);
    let last = from;

    const paint = (r: FloatRect) => {
      const el = boxRef.current;
      if (!el) return;
      el.style.left = `${r.x}px`;
      el.style.top = `${r.y}px`;
      el.style.width = `${r.w}px`;
      el.style.height = `${r.h}px`;
    };

    const onMove = (ev: MouseEvent) => {
      last = compute(ev.clientX - startX, ev.clientY - startY, ev);
      paint(last);
      if (kind === "move") {
        setSnap(snapZoneFor(ev.clientX, ev.clientY, window.innerWidth));
      }
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setGesture(null);
      const { vw, vh } = viewport();
      const zone = kind === "move" ? snapZoneFor(ev.clientX, ev.clientY, vw) : null;
      setSnap(null);
      if (zone) setFloatRect(rectForZone(zone, vw, vh), { snapped: true });
      else setFloatRect(last);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const startMove = (e: React.MouseEvent) => {
    // Belka jest jednocześnie uchwytem do przeciągania i paskiem narzędzi.
    // Gest zaczyna się od `preventDefault()`, co ZABIJA kliknięcie — więc na
    // przyciskach (nowa rozmowa, zwiń, przypnij, zamknij) po prostu go nie
    // zaczynamy. Bez tego cała belka pływaka jest martwa.
    if ((e.target as HTMLElement).closest("button, a, input, textarea, select")) return;

    // Dwuklik w belkę = powrót do domyślnego położenia i rozmiaru. Liczymy go
    // sami z `detail`, bo gest przeciągania zaczyna się od `preventDefault()`,
    // po którym przeglądarka potrafi nie wysłać zdarzenia `dblclick`.
    if (e.detail >= 2) {
      const { vw, vh } = viewport();
      setMinimized(false);
      restoreFloatRect(defaultRect(vw, vh));
      return;
    }

    // Okno przyciągnięte do krawędzi (albo rozciągnięte na cały ekran) nie ma
    // dokąd się ruszyć — clamp trzyma je na miejscu. Złapanie belki ODCZEPIA je:
    // wraca do swojej wielkości i jedzie za kursorem, jak w Windows.
    const base = restoreRect
      ? (() => {
          const { vw, vh } = viewport();
          const w = restoreRect.w;
          const h = restoreRect.h;
          // Kursor zostaje w tym samym miejscu belki, proporcjonalnie.
          const grabRatio = rect.w > 0 ? (e.clientX - rect.x) / rect.w : 0.5;
          const next = clampRect(
            { w, h, x: e.clientX - w * grabRatio, y: Math.max(0, e.clientY - 14) },
            vw,
            vh,
          );
          restoreFloatRect(next);
          return next;
        })()
      : rect;

    runGesture(e, "move", (dx, dy) => {
      const { vw, vh } = viewport();
      return clampRect({ ...base, x: base.x + dx, y: base.y + dy }, vw, vh);
    }, base);
  };

  const startResize = (e: React.MouseEvent, dir: ResizeDir) =>
    runGesture(e, "resize", (dx, dy) => {
      const { vw, vh } = viewport();
      return resizeRect(rect, dir, dx, dy, vw, vh);
    });

  const snapPreview = snap ? rectForZone(snap, window.innerWidth, window.innerHeight) : null;

  return createPortal(
    <>
      {/* Podgląd miejsca, w które okno wskoczy po puszczeniu przy krawędzi. */}
      {snapPreview && (
        <div
          style={{
            left: snapPreview.x,
            top: snapPreview.y,
            width: snapPreview.w,
            height: snapPreview.h,
          }}
          className="pointer-events-none fixed z-40 border-2 border-red-500 bg-red-500/10"
        />
      )}

      <div
        ref={boxRef}
        style={{
          left: rect.x,
          top: rect.y,
          width: rect.w,
          height: minimized ? undefined : rect.h,
        }}
        // z-40: nad stroną, ale POD modalami (z-50) — okno nie może zasłonić
        // bramki zatwierdzania, którą samo wywołuje.
        className={`fixed z-40 flex flex-col border border-gray-300 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.28)] dark:border-gray-600 dark:bg-gray-900 ${
          gesture ? "select-none" : ""
        }`}
      >
        <AronFloatWindow onHeaderMouseDown={startMove} />

        {!minimized &&
          HANDLES.map((h) => (
            <div
              key={h.dir}
              onMouseDown={(e) => startResize(e, h.dir)}
              className={`absolute ${h.cls}`}
            />
          ))}
      </div>

      {/* W trakcie gestu przezroczysta warstwa trzyma kursor i nie pozwala
          mapom ani ramkom przechwycić myszy. */}
      {gesture && (
        <div
          className={`fixed inset-0 z-50 ${
            gesture === "move" ? "cursor-grabbing" : "cursor-default"
          }`}
        />
      )}
    </>,
    document.body,
  );
}
